import { z } from 'zod';
import type { AiModelPolicy } from './model-policy.js';

export const aiProviderIdSchema = z.enum([
  'yandex',
  'deepseek',
  'openai',
  'anthropic',
  'openrouter',
  'openai_compatible',
  'remote_runner',
]);
export type AiProviderId = z.infer<typeof aiProviderIdSchema>;

export const aiModelRefSchema = z.object({
  provider: aiProviderIdSchema,
  model: z.string().min(1).max(128),
  /** Selects a concrete named connection when a provider has more than one. */
  connectionId: z.string().min(1).max(128).optional(),
});
export type AiModelRef = z.infer<typeof aiModelRefSchema>;

export const aiRoleRuntimeConfigSchema = z.object({
  roleKey: z.string().min(1).max(64),
  primary: aiModelRefSchema,
  fallback: aiModelRefSchema.nullable(),
  timeoutMs: z.number().int().positive().max(120_000),
  maxOutputTokens: z.number().int().positive().max(16_000),
  promptVersion: z.number().int().positive(),
  configVersion: z.number().int().positive().optional(),
});
export type AiRoleRuntimeConfig = z.infer<typeof aiRoleRuntimeConfigSchema>;

export const aiRuntimeErrorCodeSchema = z.enum([
  'ai_role_unconfigured',
  'ai_provider_unavailable',
  'ai_provider_timeout',
  'ai_transcription_unavailable',
  'invalid_audio',
  'ai_config_invalid',
  'ai_secrets_unavailable',
]);
export type AiRuntimeErrorCode = z.infer<typeof aiRuntimeErrorCodeSchema>;

export type AiProviderAttemptResult = {
  provider: AiProviderId;
  model: string;
  attempt: 1 | 2;
  success: boolean;
  latencyMs: number;
  failureCode?: AiRuntimeErrorCode | 'provider_http_4xx' | 'provider_http_5xx' | 'provider_network';
};

export type AiConversationTurn = {
  role: 'user' | 'assistant';
  content: string;
};

export type AiTextCompleteInput = {
  systemPrompt: string;
  /** Latest user utterance (may duplicate last turn — OK). */
  userPrompt: string;
  /** Oldest → newest, before latest user message. */
  conversationTurns?: AiConversationTurn[];
  roleConfig: AiRoleRuntimeConfig;
  /** Already-resolved model routing snapshot. Avoids re-reading role config per model call. */
  modelPolicy?: AiModelPolicy;
  /** Default provider temperature when omitted (understanding 0.2 / response 0.5). */
  temperature?: number;
};

export type AiTextCompleteResult = {
  text: string;
  telemetry: AiProviderAttemptResult[];
};

export interface AiTextGateway {
  complete(input: AiTextCompleteInput): Promise<AiTextCompleteResult>;
}

export type AiSpeechToTextInput = {
  audioBuffer: Buffer;
  mimeType: string;
  durationSec?: number;
};

export interface AiSpeechToTextProvider {
  transcribe(input: AiSpeechToTextInput): Promise<{ transcript: string }>;
};

export class AiRuntimeError extends Error {
  /**
   * Попытки провайдера, если ошибка родилась в шлюзе.
   *
   * Задержки неудачных вызовов — такой же замер, как и удачных: без них p95
   * считается только по тому, что ответило, и таймауты из картины исчезают.
   */
  telemetry?: readonly AiProviderAttemptResult[];

  constructor(
    public readonly code: AiRuntimeErrorCode,
    message?: string,
  ) {
    super(message ?? code);
    this.name = 'AiRuntimeError';
  }
}
